import React, { useState } from "react";

/**
 * Lightweight inline-SVG charts — no charting dependency.
 *
 * Colours are categorical slots 1–3 of the validated palette. Verified against
 * this app's white card surface with all pairs in play: CVD ΔE 9.2 (deutan),
 * normal-vision ΔE 24.0. Aqua sits at 2.82:1 contrast, which obligates visible
 * direct labels — every series here carries its value in text, so identity is
 * never colour-alone.
 */
export const SERIES = ["#2a78d6", "#eb6834", "#1baf7a"];

const GRID = "#e1e0d9";
const AXIS = "#c3c2b7";
const MUTED = "#898781";

const Empty = ({ msg }) => <div className="chart-empty">{msg}</div>;

// Round an axis maximum up to a readable step (1/2/5 × 10ⁿ).
function niceMax(v) {
  if (v <= 4) return Math.max(1, Math.ceil(v));
  const mag = 10 ** Math.floor(Math.log10(v));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  const short = MON[Number(m) - 1] || key;
  return short === "Jan" ? `${short} ${y.slice(2)}` : short;
};

/* ------------------------------------------------------------------ Donut */
/* Part-to-whole at a glance, ≤ 6 segments. Total is the hero figure. */
export function Donut({ data, title, unit = "consents" }) {
  const [hi, setHi] = useState(null);
  const rows = (data || []).map((d, i) => ({
    label: d.label,
    value: Number(d.value) || 0,
    color: SERIES[i % SERIES.length],
  }));
  const total = rows.reduce((s, d) => s + d.value, 0);

  const R = 66;
  const SW = 20;
  const C = 2 * Math.PI * R;
  const live = rows.filter((r) => r.value > 0);
  const GAP = live.length > 1 ? 3 : 0; // 2px+ surface gap between fills

  let acc = 0;
  const segs = rows.map((r) => {
    const frac = total ? r.value / total : 0;
    const seg = { ...r, frac, len: Math.max(0.01, frac * C - GAP), off: -acc * C };
    acc += frac;
    return seg;
  });

  return (
    <div className="card chart">
      <div className="chart-h">{title}</div>
      {total === 0 ? (
        <Empty msg="No consent records yet" />
      ) : (
        <div className="donut-wrap">
          <svg
            viewBox="0 0 180 180"
            className="donut"
            role="img"
            aria-label={`${title}. ${rows.map((r) => `${r.label}: ${r.value}`).join(". ")}`}
          >
            <g transform="rotate(-90 90 90)">
              <circle cx="90" cy="90" r={R} fill="none" stroke="#eef2f6" strokeWidth={SW} />
              {segs
                .filter((s) => s.value > 0)
                .map((s) => (
                  <circle
                    key={s.label}
                    cx="90"
                    cy="90"
                    r={R}
                    fill="none"
                    stroke={s.color}
                    strokeWidth={hi === s.label ? SW + 5 : SW}
                    strokeDasharray={`${s.len} ${C - s.len}`}
                    strokeDashoffset={s.off}
                    onMouseEnter={() => setHi(s.label)}
                    onMouseLeave={() => setHi(null)}
                  />
                ))}
            </g>
            <text x="90" y="88" textAnchor="middle" className="donut-n">
              {hi ? segs.find((s) => s.label === hi).value : total}
            </text>
            <text x="90" y="106" textAnchor="middle" className="donut-l">
              {hi || unit}
            </text>
          </svg>

          <ul className="legend">
            {segs.map((s) => (
              <li
                key={s.label}
                className={hi === s.label ? "on" : ""}
                onMouseEnter={() => setHi(s.label)}
                onMouseLeave={() => setHi(null)}
              >
                <span className="sw" style={{ background: s.color }} />
                <span className="lb">{s.label}</span>
                <b>{s.value}</b>
                <span className="pc">{total ? Math.round(s.frac * 100) : 0}%</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Line chart */
/* Trend over time, 3 series on ONE axis (never a second y-scale). */
export function LineChart({ months, series, title }) {
  const [ix, setIx] = useState(-1);
  const W = 760;
  const H = 300;
  const L = 44;
  const R = 54; // room for the direct end-labels
  const T = 16;
  const B = 40;
  const iw = W - L - R;
  const ih = H - T - B;

  const ms = months || [];
  const ss = (series || []).map((s, i) => ({ ...s, color: SERIES[i % SERIES.length] }));
  const n = ms.length;
  const total = ss.reduce((a, s) => a + s.values.reduce((b, v) => b + v, 0), 0);

  const max = niceMax(Math.max(1, ...ss.flatMap((s) => s.values)));
  const x = (i) => L + (n <= 1 ? iw / 2 : (i * iw) / (n - 1));
  const y = (v) => T + ih - (v / max) * ih;
  const TICKS = 4;

  // End-labels share a y when two series finish on the same value, so nudge them
  // apart (and back inside the plot) rather than stacking them illegibly.
  const endLabels = (() => {
    const MIN = 14;
    const items = ss
      .map((s) => ({ label: s.label, color: s.color, v: s.values[n - 1], y: y(s.values[n - 1]) }))
      .sort((a, b) => a.y - b.y);
    for (let i = 1; i < items.length; i += 1) {
      if (items[i].y - items[i - 1].y < MIN) items[i].y = items[i - 1].y + MIN;
    }
    const over = items.length ? items[items.length - 1].y - (T + ih) : 0;
    if (over > 0) items.forEach((it) => { it.y -= over; });
    return items;
  })();

  return (
    <div className="card chart">
      <div className="chart-h">{title}</div>
      {total === 0 ? (
        <Empty msg="No activity recorded in the last 12 months" />
      ) : (
        <>
          <ul className="legend row">
            {ss.map((s) => (
              <li key={s.label}>
                <span className="sw" style={{ background: s.color }} />
                <span className="lb">{s.label}</span>
              </li>
            ))}
          </ul>

          <div className="line-wrap">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="line"
              role="img"
              aria-label={`${title}. ${ss.map((s) => `${s.label}: ${s.values.join(", ")}`).join(". ")}`}
            >
              {/* recessive grid + y labels */}
              {Array.from({ length: TICKS + 1 }, (_, t) => {
                const v = (max / TICKS) * t;
                return (
                  <g key={t}>
                    <line x1={L} x2={L + iw} y1={y(v)} y2={y(v)} stroke={GRID} strokeWidth="1" />
                    <text x={L - 10} y={y(v) + 4} textAnchor="end" className="ax">
                      {Math.round(v)}
                    </text>
                  </g>
                );
              })}
              <line x1={L} x2={L + iw} y1={y(0)} y2={y(0)} stroke={AXIS} strokeWidth="1" />

              {/* x labels */}
              {ms.map((m, i) => (
                <text key={m} x={x(i)} y={H - 16} textAnchor="middle" className="ax">
                  {monthLabel(m)}
                </text>
              ))}

              {/* crosshair */}
              {ix >= 0 && (
                <line x1={x(ix)} x2={x(ix)} y1={T} y2={T + ih} stroke={AXIS} strokeWidth="1" strokeDasharray="3 3" />
              )}

              {/* 2px lines, 8px markers with a 2px surface ring */}
              {ss.map((s) => (
                <g key={s.label}>
                  <polyline
                    fill="none"
                    stroke={s.color}
                    strokeWidth="2"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(" ")}
                  />
                  {s.values.map((v, i) => (
                    <circle
                      key={i}
                      cx={x(i)}
                      cy={y(v)}
                      r={ix === i ? 5 : 4}
                      fill={s.color}
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  ))}
                </g>
              ))}

              {/* direct end-labels — de-collided so identity never relies on colour */}
              {endLabels.map((e) => (
                <text key={e.label} x={L + iw + 9} y={e.y + 4} className="endlab" fill={e.color}>
                  {e.v}
                </text>
              ))}

              {/* hover bands */}
              {ms.map((m, i) => (
                <rect
                  key={m}
                  x={x(i) - iw / (n - 1) / 2}
                  y={T}
                  width={iw / (n - 1)}
                  height={ih}
                  fill="transparent"
                  onMouseEnter={() => setIx(i)}
                  onMouseLeave={() => setIx(-1)}
                />
              ))}
            </svg>

            {ix >= 0 && (
              <div
                className="tip"
                style={{
                  left: `${(x(ix) / W) * 100}%`,
                  transform: x(ix) > W * 0.65 ? "translate(-108%, 0)" : "translate(8px, 0)",
                }}
              >
                <div className="tip-h">{monthLabel(ms[ix])}</div>
                {ss.map((s) => (
                  <div key={s.label} className="tip-r">
                    <span className="sw" style={{ background: s.color }} />
                    <span className="lb">{s.label}</span>
                    <b>{s.values[ix]}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------- Bar chart */
/* Magnitude comparison — one hue, length carries the value. */
export function BarChart({ data, title, empty }) {
  const [hi, setHi] = useState(null);
  const rows = data || [];
  const max = Math.max(1, ...rows.map((r) => r.value));

  return (
    <div className="card chart">
      <div className="chart-h">{title}</div>
      {rows.length === 0 ? (
        <Empty msg={empty || "No data yet"} />
      ) : (
        <div className="bars" role="img" aria-label={`${title}. ${rows.map((r) => `${r.label}: ${r.value}`).join(". ")}`}>
          {rows.map((r) => (
            <div
              key={r.label}
              className={`bar-row${hi === r.label ? " on" : ""}`}
              onMouseEnter={() => setHi(r.label)}
              onMouseLeave={() => setHi(null)}
            >
              <div className="bar-l" title={r.label}>{r.label}</div>
              <div className="bar-t">
                <div
                  className="bar-f"
                  style={{ width: `${Math.max(2, (r.value / max) * 100)}%`, background: SERIES[0] }}
                />
              </div>
              <div className="bar-v">{r.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { MUTED };
